// src/services/sightWordsWorksheetService.ts
import { selectSightWordsForMood } from './adapters/sightWordsDataAdapter'
import type { MoodType } from '../types/phonicsWorksheet'
import type { WorksheetData, Activity } from '../types/phonicsWorksheet'

interface SightWordConfig {
  mood: MoodType
}

export async function generateSightWordsWorksheet(config: SightWordConfig): Promise<WorksheetData> {
  const { mood } = config
  
  // Select words based on mood
  const wordSelection = selectSightWordsForMood(mood)
  
  // Build activities (same 4 types as phonics, same word-row approach)
  const activities: Activity[] = [
    {
      type: 'circle_match',
      name: 'Circle the Word You Know',
      description: 'Find this word in each row',
      instructions: `Find the word "${wordSelection.words[0]}" in each row. Circle it.`,
      words: [wordSelection.words[0], ...wordSelection.words.slice(1, 4)],
      cognitiveLoad: 'low',
      motorDemand: 'minimal'
    },
    {
      type: 'trace_one',
      name: 'Trace Just One',
      description: 'Pick one word to trace 3 times',
      instructions: 'Pick ONE word from below to trace 3 times. Just one is perfect.',
      words: wordSelection.words.slice(0, 3),
      cognitiveLoad: 'low',
      motorDemand: 'moderate'
    },
    {
      type: 'connect_pairs',
      name: 'Connect the Pairs',
      description: 'Match words that are exactly the same',
      instructions: 'Draw lines to connect words that are exactly the same.',
      words: wordSelection.words.slice(0, 5),
      cognitiveLoad: 'low',
      motorDemand: 'minimal'
    },
    {
      type: 'find_your_word',
      name: 'Find Your Word',
      description: 'Circle the word every time you see it',
      instructions: `Circle the word "${wordSelection.words[0]}" every time you see it in the rows below.`,
      words: wordSelection.words.slice(0, 4),
      cognitiveLoad: 'medium',
      motorDemand: 'minimal'
    }
  ]
  
  // Mood-based styling (same as phonics)
  const stylingConfig = {
    overwhelmed: {
      colorScheme: ['#94BBE9', '#E3F2FD'],
      fontSize: '22px',
      spacing: 'relaxed',
      encouragingMessages: ['Take your time', 'Even one word is progress'],
      movementBreaks: ['Stand up and stretch']
    },
    high_energy: {
      colorScheme: ['#FFC107', '#FFF9C4'],
      fontSize: '18px',
      spacing: 'compact',
      encouragingMessages: ['Great energy today!', 'Keep that focus going'],
      movementBreaks: ['Jump 5 times', 'Touch your toes']
    },
    steady: {
      colorScheme: ['#9C27B0', '#F3E5F5'],
      fontSize: '20px',
      spacing: 'balanced',
      encouragingMessages: ['You\'re doing well', 'Steady progress'],
      movementBreaks: ['Take 3 deep breaths']
    }
  }
  
  return {
    config: { 
      pattern: 'sight_words' as any, 
      mood, 
      skipOptions: { noWriting: false, noRhyming: true, visualOnly: false } 
    },
    words: wordSelection.words,
    activities,
    estimatedTime: mood === 'overwhelmed' ? 5 : mood === 'steady' ? 10 : 15,
    wordCount: wordSelection.wordCount,
    activityCount: 4,
    styling: stylingConfig[mood],
    parentNotes: {
      why: `These sight words cannot be sounded out - they must be memorized through repeated visual exposure. ${
        mood === 'overwhelmed' ? 'Starting with just the most common words (appear in 25% of all text) to build confidence.' :
        mood === 'steady' ? 'This mix practices familiar high-frequency words plus introduces new ones.' :
        'This set challenges across multiple frequency tiers for active learners ready to expand vocabulary.'
      }`,
      how: `Sight words need 20-30+ visual exposures for ND learners (vs 5-7 for typical readers). These activities repeat the same words in different formats - word rows, matching, tracing - to build instant recognition without sounding out. ${
        mood === 'overwhelmed' ? 'Keep it light. Even recognizing 2 of the 3 words instantly is excellent progress.' :
        'Encourage quick visual recognition rather than trying to decode letter by letter.'
      }`,
      expectations: `Success means instant visual recognition - the child sees "the" and knows it immediately, without thinking. ${
        mood === 'overwhelmed' ? 'If they recognize 2 words instantly by the end, that\'s a win.' :
        mood === 'steady' ? 'Recognizing 4-5 words quickly shows strong progress.' :
        'Fluent instant recognition of 6+ words shows excellent visual memory building.'
      }`
    }
  }
}